import { useEffect, useState, useMemo, useCallback } from 'react';
import { FetchPdc } from '../api/FetchPdc';
import PdpIcon from './PdpIcon';
import '../css/Pdc.css';
import { useNavigation } from '../function/navigation';
import ProgressBar from './ProgressBar';
import { UnitConversion } from '../inputs/UnitConversion';

function ChoosePdc({
  allPdc,
  setAllPdc,
  inputs,
  setInputs,
  interceptorsReady,
  setLaunchId,
}) {
  const { dispatch } = useNavigation();

  const [presType, setPresType] = useState('grid');
  const [hoveredPdpId, setHoveredPdpId] = useState(null);
  const [currentPdc, setCurrentPDC] = useState(inputs.pdc);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSpecs, setShowSpecs] = useState(false);
  const [pdcMode, setPdcMode] = useState('market');
  const [tableSort, setTableSort] = useState({
    key: 'pdc-id',
    direction: 'asc',
  });

  const matchesPdcMode = (item) => {
    if (item.id === 0) return pdcMode !== 'svl';
    if (pdcMode === 'svl') return item.svl === true;
    const customerFlag = Number(item.customer_pdc);
    if (pdcMode === 'customer') return customerFlag === 1;
    return customerFlag === 0 && item.customer_pdc !== null;
  };

  const subPdc = useMemo(
    () => allPdc.filter(matchesPdcMode),
    [allPdc, pdcMode]
  );

  const hasSvlPdc = useMemo(
    () => allPdc.some((item) => item.id === -10 || item.svl === true),
    [allPdc]
  );

  const uniqueIndustries = useMemo(
    () =>
      [...new Set(subPdc.map((item) => item.industry_name))].filter(Boolean),
    [subPdc]
  );
  const uniqueFunctions = useMemo(
    () =>
      [...new Set(subPdc.map((item) => item.function_name))].filter(Boolean),
    [subPdc]
  );
  const uniquePdes = useMemo(
    () => [...new Set(subPdc.map((item) => item.pde_name))].filter(Boolean),
    [subPdc]
  );
  const uniqueFlow = useMemo(
    () => [...new Set(subPdc.map((item) => item.flow))].filter(Boolean),
    [subPdc]
  );
  const uniqueTransfer = useMemo(
    () => [...new Set(subPdc.map((item) => item.transfer))].filter(Boolean),
    [subPdc]
  );
  const uniquePerformance = useMemo(
    () =>
      [...new Set(subPdc.map((item) => item.performance))].filter(
        (item) => item !== null && item !== undefined
      ),
    [subPdc]
  );

  const baseFilter = useMemo(
    () => ({
      industry: uniqueIndustries,
      function: uniqueFunctions,
      pde: uniquePdes,
      flow: uniqueFlow,
      transfer: uniqueTransfer,
      performance: uniquePerformance,
    }),
    [
      uniqueIndustries,
      uniqueFunctions,
      uniquePdes,
      uniqueFlow,
      uniqueTransfer,
      uniquePerformance,
    ]
  );

  const [selectedFilters, setSelectedFilters] = useState(baseFilter);

  const handleClickPdc = () => {
    const thisPdc = allPdc.find((pdc) => pdc.id === currentPdc);
    setInputs((prev) => ({
      ...prev,
      pdc: currentPdc,
      industry: thisPdc?.industry_name ?? '',
      function: thisPdc?.function_name ?? '',
    }));
    dispatch({ type: 'mainPage', payload: 'machine' });
  };

  useEffect(() => {
    setSelectedFilters(baseFilter);
  }, [pdcMode, baseFilter]);

  useEffect(() => {
    if (!hasSvlPdc && pdcMode === 'svl') {
      setPdcMode('market');
    }
  }, [hasSvlPdc, pdcMode]);

  const defaultValue = {
    function: 'Elevation',
    pde: 'Dry and clean',
    flow: 'Single File Spaced',
    transfer: 'Head to Head',
    performance: 0,
  };

  const [showSelection, setShowSelection] = useState(true);

  const isPhone = typeof window !== 'undefined' && window.innerWidth < 600;

  const fetchPdc = useCallback(async () => {
    try {
      const result = await FetchPdc({
        level: inputs?.crm_info?.user_level, // temporary add level
        force: true,
      });
      setAllPdc(result);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [inputs?.crm_info?.user_level, setAllPdc]);

  useEffect(() => {
    if (interceptorsReady && allPdc.length === 0) fetchPdc();
  }, [interceptorsReady, allPdc.length, fetchPdc]);

  useEffect(() => {
    fetchPdc();
  }, [inputs.crm_info?.user_level, fetchPdc]);

  const handlePdcClick = (newPdc) => setCurrentPDC(newPdc.id);

  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => {
      const prevValues = prev[category] || [];

      if (prevValues.includes(value)) {
        return {
          ...prev,
          [category]: prevValues.filter((v) => v !== value),
        };
      }

      return {
        ...prev,
        [category]: [...prevValues, value],
      };
    });
  };

  const matchesFilters = (item) =>
    item.id === 0 ||
    (pdcMode === 'svl' && item.svl === true) ||
    (selectedFilters.industry.includes(item.industry_name) &&
      selectedFilters.function.includes(item.function_name) &&
      selectedFilters.pde.includes(item.pde_name) &&
      selectedFilters.flow.includes(item.flow) &&
      selectedFilters.transfer.includes(item.transfer) &&
      selectedFilters.performance.includes(item.performance));

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const matchesSearch = (item) => {
    if (!normalizedSearch) return true;

    const pdcName = String(item.name || '').toLowerCase();
    const pdpValues = (item.pdp_list || [])
      .flatMap((pdp) => [pdp.type, pdp.name, pdp.id])
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value).toLowerCase())
      .join(' ');

    return `${pdcName} ${pdpValues}`.includes(normalizedSearch);
  };

  const filteredPdc = subPdc.filter(matchesFilters).filter(matchesSearch);

  const renderFilterGroup = (title, options, category) => {
    if (options.length <= 1) return;

    const selectedValues = selectedFilters[category] || [];

    return (
      <div className={`pdc-filter-group ${category}`}>
        <div className="pdc-filter-title">{title}</div>

        {options.map((option) => (
          <div
            className="pdc-checkbox-container"
            key={`checkbox-${category}-${option}`}
            onClick={() => toggleFilter(category, option)}
          >
            <div className="checkbox-container-pdc">
              <input
                type="checkbox"
                name={`${category}-${option}`}
                checked={selectedValues.includes(option)}
                onChange={() => toggleFilter(category, option)}
                className={`checkbox-input`}
                id={`checkbox-${category}-${option}`}
              />
              <label
                htmlFor={`checkbox-${category}-${option}`}
                className="checkbox-label"
              ></label>
            </div>
            <p className="pdc-filter-label">
              {option === 0 ? 'False' : option === 1 ? 'True' : option}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const hasSpecification = filteredPdc.some((opt) => opt.specifications);
  const maxHeightUnit = UnitConversion(
    null,
    inputs.unit_system,
    'mm'
  ).displayedUnit;
  const sizeUnit = UnitConversion(null, inputs.unit_system, 'mm').displayedUnit;
  const maxSpeedUnit = UnitConversion(
    null,
    inputs.unit_system,
    'm/min'
  ).displayedUnit;
  const weightUnit = UnitConversion(
    null,
    inputs.unit_system,
    'kg'
  ).displayedUnit;

  const renderSpecValue = (value, unit) => {
    const { displayedValue } = UnitConversion(value, inputs.unit_system, unit);
    const roundedValue =
      typeof displayedValue === 'number'
        ? Math.round(displayedValue)
        : displayedValue;
    return String(roundedValue ?? '').replace(/\//g, '/\u200B');
  };

  const withUnit = (value, unit) => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === '-'
    ) {
      return '-';
    }
    return `${value} ${unit}`;
  };

  const toNumeric = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const convertAndRound = (value, unit) => {
    const numeric = toNumeric(value);
    if (numeric === null) return null;
    const { displayedValue } = UnitConversion(
      numeric,
      inputs.unit_system,
      unit
    );
    return typeof displayedValue === 'number'
      ? Math.round(displayedValue)
      : null;
  };

  const formatRange = (minValue, maxValue, unit) => {
    const minDisplay = convertAndRound(minValue, unit);
    const maxDisplay = convertAndRound(maxValue, unit);
    if (minDisplay === null && maxDisplay === null) return '-';
    if (minDisplay === null) return `<= ${maxDisplay}`;
    if (maxDisplay === null) return `>= ${minDisplay}`;
    return `${minDisplay}-${maxDisplay}`;
  };

  const buildPdpBounds = (pdpList = []) => {
    const fields = ['length', 'width', 'height', 'weight'];
    const bounds = {
      inclineMax: null,
      ranges: {
        length: { min: null, max: null },
        width: { min: null, max: null },
        height: { min: null, max: null },
        weight: { min: null, max: null },
      },
    };

    for (const pdp of pdpList) {
      const incline = toNumeric(pdp.max_incline);
      if (incline !== null) {
        bounds.inclineMax =
          bounds.inclineMax === null
            ? incline
            : Math.max(bounds.inclineMax, incline);
      }

      for (const field of fields) {
        const minKey = `min_${field}`;
        const maxKey = `max_${field}`;
        const minValue = toNumeric(pdp[minKey]);
        const maxValue = toNumeric(pdp[maxKey]);

        if (minValue !== null) {
          bounds.ranges[field].min =
            bounds.ranges[field].min === null
              ? minValue
              : Math.min(bounds.ranges[field].min, minValue);
        }
        if (maxValue !== null) {
          bounds.ranges[field].max =
            bounds.ranges[field].max === null
              ? maxValue
              : Math.max(bounds.ranges[field].max, maxValue);
        }
      }
    }

    return bounds;
  };

  const renderPdpTooltip = (pdp) => {
    const sizeUnit = UnitConversion(
      null,
      inputs.unit_system,
      'mm'
    ).displayedUnit;
    const weightUnit = UnitConversion(
      null,
      inputs.unit_system,
      'kg'
    ).displayedUnit;
    const inclineValue = toNumeric(pdp.max_incline);

    return (
      <div className="pdp-tooltip-content">
        <div className="pdp-tooltip-title">{pdp.type}</div>
        <div>{`length: ${formatRange(pdp.min_length, pdp.max_length, 'mm')} ${sizeUnit}`}</div>
        <div>{`width: ${formatRange(pdp.min_width, pdp.max_width, 'mm')} ${sizeUnit}`}</div>
        <div>{`height: ${formatRange(pdp.min_height, pdp.max_height, 'mm')} ${sizeUnit}`}</div>
        <div>{`weight: ${formatRange(pdp.min_weight, pdp.max_weight, 'kg')} ${weightUnit}`}</div>
        <div>{`Incline: ${inclineValue === null ? '-' : Math.round(inclineValue)} °`}</div>
      </div>
    );
  };

  const filterGroupConfigs = [
    { title: 'Industry', options: uniqueIndustries, category: 'industry' },
    { title: 'Function', options: uniqueFunctions, category: 'function' },
    { title: 'Environment', options: uniquePdes, category: 'pde' },
    { title: 'Flow', options: uniqueFlow, category: 'flow' },
    { title: 'Transfer', options: uniqueTransfer, category: 'transfer' },
  ];

  const availableFilterGroups = filterGroupConfigs.filter(
    ({ options }) => options.length > 1
  );

  const tableSpecColumns = [
    {
      key: 'max-height',
      header: `Max height [${maxHeightUnit}]`,
      render: (option) =>
        renderSpecValue(option.specifications?.max_height, 'mm'),
    },
    {
      key: 'max-speed',
      header: `Max speed [${maxSpeedUnit}]`,
      render: (option) =>
        renderSpecValue(option.specifications?.centerline_speed, 'm/min'),
    },
    {
      key: 'max-incline',
      header: 'Max incline [°]',
      render: (_, pdpBounds) =>
        pdpBounds.inclineMax === null ? '-' : Math.round(pdpBounds.inclineMax),
    },
    {
      key: 'product-length',
      header: `Product length [${sizeUnit}]`,
      render: (_, pdpBounds) =>
        formatRange(
          pdpBounds.ranges.length.min,
          pdpBounds.ranges.length.max,
          'mm'
        ),
    },
    {
      key: 'product-width',
      header: `Product width [${sizeUnit}]`,
      render: (_, pdpBounds) =>
        formatRange(
          pdpBounds.ranges.width.min,
          pdpBounds.ranges.width.max,
          'mm'
        ),
    },
    {
      key: 'product-height',
      header: `Product height [${sizeUnit}]`,
      render: (_, pdpBounds) =>
        formatRange(
          pdpBounds.ranges.height.min,
          pdpBounds.ranges.height.max,
          'mm'
        ),
    },
    {
      key: 'product-weight',
      header: `Product weight [${weightUnit}]`,
      render: (_, pdpBounds) =>
        formatRange(
          pdpBounds.ranges.weight.min,
          pdpBounds.ranges.weight.max,
          'kg'
        ),
    },
  ];

  const tileTagConfigs = [
    {
      key: 'industry',
      show: (option) => Boolean(option.industry_name),
      className: 'industry',
      label: (option) => option.industry_name,
    },
    {
      key: 'function',
      show: (option) =>
        Boolean(option.function_name) &&
        option.function_name !== defaultValue.function,
      className: 'function',
      label: (option) => option.function_name,
    },
    {
      key: 'pde',
      show: (option) =>
        Boolean(option.pde_name) && option.pde_name !== defaultValue.pde,
      className: 'pde',
      label: (option) => option.pde_name,
    },
    {
      key: 'flow',
      show: (option) =>
        Boolean(option.flow) && option.flow !== defaultValue.flow,
      className: 'flow',
      label: (option) => option.flow,
    },
    {
      key: 'transfer',
      show: (option) =>
        Boolean(option.transfer) && option.transfer !== defaultValue.transfer,
      className: 'transfer',
      label: (option) => option.transfer,
    },
    {
      key: 'performance',
      show: (option) =>
        option.performance !== null &&
        option.performance !== undefined &&
        option.performance !== defaultValue.performance,
      className: 'performance',
      label: () => 'Performance',
    },
  ];

  const getTilePrimarySpecs = (option, pdpBounds) => [
    {
      key: 'max-height',
      label: 'max height',
      value: withUnit(
        renderSpecValue(option.specifications?.max_height, 'mm'),
        maxHeightUnit
      ),
    },
    {
      key: 'max-speed',
      label: 'max speed',
      value: withUnit(
        renderSpecValue(option.specifications?.centerline_speed, 'm/min'),
        maxSpeedUnit
      ),
    },
    {
      key: 'max-incline',
      label: 'max incline angle',
      value:
        pdpBounds.inclineMax === null
          ? '-'
          : `${Math.round(pdpBounds.inclineMax)} °`,
    },
  ];

  const getTileProductSpecs = (pdpBounds) => [
    {
      key: 'product-length',
      label: 'product length',
      value: withUnit(
        formatRange(
          pdpBounds.ranges.length.min,
          pdpBounds.ranges.length.max,
          'mm'
        ),
        sizeUnit
      ),
    },
    {
      key: 'product-width',
      label: 'width',
      value: withUnit(
        formatRange(
          pdpBounds.ranges.width.min,
          pdpBounds.ranges.width.max,
          'mm'
        ),
        sizeUnit
      ),
    },
    {
      key: 'product-height',
      label: 'height',
      value: withUnit(
        formatRange(
          pdpBounds.ranges.height.min,
          pdpBounds.ranges.height.max,
          'mm'
        ),
        sizeUnit
      ),
    },
    {
      key: 'product-weight',
      label: 'weight',
      value: withUnit(
        formatRange(
          pdpBounds.ranges.weight.min,
          pdpBounds.ranges.weight.max,
          'kg'
        ),
        weightUnit
      ),
    },
  ];

  const getTableSortValue = (option, pdpBounds, key) => {
    switch (key) {
      case 'pdc-id':
        return option.id;
      case 'configuration':
        return option.name || '';
      case 'industry':
        return option.industry_name || '';
      case 'function':
        return option.function_name || '';
      case 'environment':
        return option.pde_name || '';
      case 'flow':
        return option.flow || '';
      case 'transfer':
        return option.transfer || '';
      case 'performance':
        return option.performance ? 1 : 0;
      case 'pdp':
        return (option.pdp_list || []).length;
      case 'max-height':
        return toNumeric(option.specifications?.max_height);
      case 'max-speed':
        return toNumeric(option.specifications?.centerline_speed);
      case 'max-incline':
        return pdpBounds.inclineMax;
      case 'product-length':
        return {
          min: pdpBounds.ranges.length.min,
          max: pdpBounds.ranges.length.max,
        };
      case 'product-width':
        return {
          min: pdpBounds.ranges.width.min,
          max: pdpBounds.ranges.width.max,
        };
      case 'product-height':
        return {
          min: pdpBounds.ranges.height.min,
          max: pdpBounds.ranges.height.max,
        };
      case 'product-weight':
        return {
          min: pdpBounds.ranges.weight.min,
          max: pdpBounds.ranges.weight.max,
        };
      default:
        return null;
    }
  };

  const rangeSortKeys = new Set([
    'product-length',
    'product-width',
    'product-height',
    'product-weight',
  ]);

  const compareSortValues = (a, b) => {
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;

    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  const toggleTableSort = (key) => {
    setTableSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'desc' ? 'asc' : 'desc',
        };
      }
      return { key, direction: 'desc' };
    });
  };

  const baseTableColumns = [
    { key: 'configuration', header: 'Configuration' },
    { key: 'industry', header: 'Industry' },
    { key: 'function', header: 'Function' },
    { key: 'environment', header: 'Environment' },
    { key: 'flow', header: 'Flow' },
    { key: 'transfer', header: 'Transfer' },
    { key: 'performance', header: 'Performance' },
    { key: 'pdp', header: "PDP's" },
  ];

  const tableRows = useMemo(() => {
    return filteredPdc.map((option) => ({
      option,
      pdpBounds: buildPdpBounds(option.pdp_list || []),
    }));
  }, [filteredPdc]);

  const sortedTableRows = useMemo(() => {
    const rows = [...tableRows];
    rows.sort((a, b) => {
      if (tableSort.key === 'pdc-id') {
        const aIsCustomized = a.option.id === 0;
        const bIsCustomized = b.option.id === 0;
        if (aIsCustomized && !bIsCustomized) return 1;
        if (!aIsCustomized && bIsCustomized) return -1;
        return a.option.id - b.option.id;
      }

      const aValue = getTableSortValue(a.option, a.pdpBounds, tableSort.key);
      const bValue = getTableSortValue(b.option, b.pdpBounds, tableSort.key);

      if (rangeSortKeys.has(tableSort.key)) {
        const aMin = aValue?.min ?? Number.POSITIVE_INFINITY;
        const bMin = bValue?.min ?? Number.POSITIVE_INFINITY;
        const aMax = aValue?.max ?? Number.NEGATIVE_INFINITY;
        const bMax = bValue?.max ?? Number.NEGATIVE_INFINITY;

        if (tableSort.direction === 'asc') {
          if (aMin !== bMin) return aMin - bMin;
          return bMax - aMax;
        }

        if (aMax !== bMax) return bMax - aMax;
        return aMin - bMin;
      }

      const result = compareSortValues(aValue, bValue);
      return tableSort.direction === 'desc' ? -result : result;
    });
    return rows;
  }, [tableRows, tableSort]);

  const renderSortableHeader = ({ key, header }) => {
    const isActive = tableSort.key === key;
    const indicator = isActive
      ? tableSort.direction === 'desc'
        ? ' ▼'
        : ' ▲'
      : '';

    return (
      <th
        key={key}
        role="button"
        className={`pdc-sortable-header ${isActive ? 'active' : ''}`}
        onClick={() => toggleTableSort(key)}
      >
        {header}
        {indicator}
      </th>
    );
  };

  const renderTableView = () => (
    <div className="glass pdc-table-container">
      <table>
        <thead>
          <tr>
            <th>Selected</th>
            {baseTableColumns.map((column) => renderSortableHeader(column))}
            {hasSpecification && showSpecs && (
              <>
                {tableSpecColumns.map((column) => renderSortableHeader(column))}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedTableRows.map(({ option, pdpBounds }, index) => {
            return (
              <tr
                key={option.id}
                className={`select-table ${currentPdc === option.id ? 'active' : ''}`}
                onClick={() => handlePdcClick(option)}
              >
                <td>
                  <input
                    type="radio"
                    id={`pdc-option-${index}`}
                    name="pdc-option"
                    checked={currentPdc === option.id}
                    onChange={() => handlePdcClick(option)}
                  />
                </td>
                <td>{option.name}</td>
                <td>
                  {option.industry_name ? (
                    <div className="tag industry">{option.industry_name}</div>
                  ) : (
                    ''
                  )}
                </td>
                <td>
                  {option.function_name &&
                  option.function_name !== defaultValue.function ? (
                    <div className="tag function">{option.function_name}</div>
                  ) : (
                    option.function_name
                  )}
                </td>
                <td>
                  {option.pde_name && option.pde_name !== defaultValue.pde ? (
                    <div className="tag pde">{option.pde_name}</div>
                  ) : (
                    option.pde_name
                  )}
                </td>
                <td>
                  {option.flow && option.flow !== defaultValue.flow ? (
                    <div className="tag flow">{option.flow}</div>
                  ) : (
                    option.flow
                  )}
                </td>
                <td>
                  {option.transfer &&
                  option.transfer !== defaultValue.transfer ? (
                    <div className="tag transfer">{option.transfer}</div>
                  ) : (
                    option.transfer
                  )}
                </td>
                <td>
                  {option.performance &&
                  option.performance !== defaultValue.performance ? (
                    <div className="tag performance check"></div>
                  ) : (
                    ''
                  )}
                </td>
                <td className="pdp-img-cell">
                  <div className="pdp-img-row">
                    {option.pdp_list?.map((pdp) => (
                      <PdpIcon
                        key={pdp.id}
                        id={`${option.id} ${pdp.id}`}
                        pdp_id={pdp.id}
                        name={renderPdpTooltip(pdp)}
                        hoveredPdpId={hoveredPdpId}
                        setHoveredPdpId={setHoveredPdpId}
                      />
                    ))}
                  </div>
                </td>
                {hasSpecification && showSpecs && (
                  <>
                    {tableSpecColumns.map((column) => (
                      <td key={column.key}>
                        {column.render(option, pdpBounds)}
                      </td>
                    ))}
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTileView = () => (
    <div className="pdc-options">
      {filteredPdc.map((option, index) => {
        const pdpBounds = buildPdpBounds(option.pdp_list || []);
        const sizeUnit = UnitConversion(
          null,
          inputs.unit_system,
          'mm'
        ).displayedUnit;
        const weightUnit = UnitConversion(
          null,
          inputs.unit_system,
          'kg'
        ).displayedUnit;

        return (
          <div
            key={option.id}
            className={`pdc-option ${option.id === 0 ? 'customized' : ''} ${currentPdc === option.id ? 'active' : ''}`}
            onClick={() => handlePdcClick(option)}
          >
            <div className="text-row">
              <h3 className="pdc-name">{option.name}</h3>
              {option.development && (
                <div className="development-pdc">in development</div>
              )}
              <input
                type="radio"
                id={`pdc-option-${index}`}
                name="pdc-option"
                checked={currentPdc === option.id}
                onChange={() => handlePdcClick(option)}
              />
            </div>
            <div className="pdp-row">
              {option.id !== 0 && (
                <div className="tags-and-icons">
                  <div className="tags">
                    {tileTagConfigs
                      .filter((config) => config.show(option))
                      .map((config) => (
                        <div
                          key={config.key}
                          className={`tag ${config.className}`}
                        >
                          {config.label(option)}
                        </div>
                      ))}
                  </div>
                  <div className="pdp-icons">
                    {(option.pdp_list || []).map((pdp) => (
                      <PdpIcon
                        key={pdp.id}
                        id={`${option.id} ${pdp.id}`}
                        pdp_id={pdp.id}
                        name={renderPdpTooltip(pdp)}
                        hoveredPdpId={hoveredPdpId}
                        setHoveredPdpId={setHoveredPdpId}
                      />
                    ))}
                  </div>
                </div>
              )}
              {option.specifications && showSpecs && (
                <>
                  <div className="pdc-info">
                    {getTilePrimarySpecs(option, pdpBounds).map((spec) => (
                      <div key={spec.key} className="pdc-spec">
                        <div className="pdc-spec-label">{spec.label}</div>
                        <div className="pdc-spec-value">{spec.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="pdc-info">
                    {getTileProductSpecs(pdpBounds).map((spec) => (
                      <div key={spec.key} className="pdc-spec">
                        <div className="pdc-spec-label">{spec.label}</div>
                        <div className="pdc-spec-value">{spec.value}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`pdc-container ${presType === 'table' ? 'table-view' : ''}`}
    >
      <ProgressBar setLaunchId={setLaunchId} />
      <div className={`pdc-filter-section ${isPhone ? 'phone' : ''}`}>
        <div className="pdc-filter-line">
          {availableFilterGroups.length > 0 && (
            <div
              className="presentation-toggle pdc-pres-icon-wrapper"
              role="button"
              onClick={() => setShowSelection((prev) => !prev)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/filter.png`}
                alt="filter-icon"
              />
              <span className="pdc-pres-tooltip left">
                {showSelection ? 'Hide' : 'Show'} filter
              </span>
            </div>
          )}

          {showSelection && availableFilterGroups.length > 0 && (
            <div className="pdc-filter-groups">
              {availableFilterGroups.map(({ title, options, category }) =>
                renderFilterGroup(title, options, category)
              )}
            </div>
          )}
        </div>
      </div>
      <div className="pdc-main-toolbar">
        <input
          type="text"
          className="shared-search-input"
          placeholder="Search PDC or PDP"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {hasSpecification && (
          <button
            type="button"
            className={`button-secondary ${showSpecs ? 'selected' : ''}`}
            onClick={() => setShowSpecs((prev) => !prev)}
          >
            {showSpecs ? 'Hide details' : 'Show details'}
          </button>
        )}
        <div className="presentation-toggle pdc-view-toggle">
          {[
            { value: 'grid', tooltip: 'Grid View', position: 'first' },
            { value: 'table', tooltip: 'Table View', position: 'last' },
          ].map((option) => (
            <div
              key={option.value}
              className="pdc-pres-icon-wrapper"
              role="button"
              tabIndex={0}
              onClick={() => setPresType(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPresType(option.value);
                }
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/${option.value}.png`}
                alt={option.value}
                className={`${option.position} ${presType === option.value ? 'selected' : ''}`}
              />
              <span className="pdc-pres-tooltip">{option.tooltip}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="pdc-main-block">
        <div className="pdc-content-layout">
          <aside className="glass pdc-mode-aside">
            {[
              { key: 'market', label: 'Market PDC' },
              { key: 'customer', label: 'Customer PDC' },
              ...(hasSvlPdc ? [{ key: 'svl', label: 'SV-L' }] : []),
            ].map((modeOption) => (
              <button
                key={modeOption.key}
                type="button"
                className={`tab-button pdc-mode-btn ${pdcMode === modeOption.key ? 'active' : ''}`}
                onClick={() => setPdcMode(modeOption.key)}
              >
                {modeOption.label}
              </button>
            ))}
          </aside>
          <div className="pdc-content-main">
            {presType === 'table' ? renderTableView() : renderTileView()}
          </div>
        </div>
      </div>

      <div className="pdc-button-container">
        <button
          className="button-md button-primary"
          onClick={() => handleClickPdc()}
          disabled={currentPdc === null}
        >
          Configure
        </button>
      </div>
    </div>
  );
}

export default ChoosePdc;
